(() => {
  'use strict';

  const uploadChoicePattern = /\b(?:logo|photo)\b/i;
  const uploaderSelector = '[data-ph-fieldname="Logo or photo upload"]';
  const addToCartSelector = 'button[name="add"]';
  const uploadlyButtonClass = 'ph_atc_btn';
  const uploadlyCloneSelector =
    '[data-ph-cart-clone="true"], .ph_clone_atc_btn';
  const uploadErrorSelector = '[data-crc-upload-error]';

  function requiresUpload(value) {
    return uploadChoicePattern.test(String(value || ''));
  }

  function shouldShowUploader(selectedValue, groupHasUploads) {
    return Boolean(groupHasUploads && requiresUpload(selectedValue));
  }

  function getUploadSelection() {
    const radios = Array.from(
      document.querySelectorAll('input[type="radio"]')
    );
    const uploadGroupNames = new Set(
      radios
        .filter((candidate) => requiresUpload(candidate.value))
        .map((candidate) => candidate.name)
    );
    const selected = radios.find(
      (candidate) =>
        candidate.checked && uploadGroupNames.has(candidate.name)
    );

    return {
      groupHasUploads: uploadGroupNames.size > 0,
      selectedValue: selected ? selected.value : '',
    };
  }

  function setUploadlyCloneState(clone, visible) {
    if (clone.getAttribute('role') !== 'button') {
      clone.setAttribute('role', 'button');
    }

    if (clone.getAttribute('aria-label') !== 'Add to cart') {
      clone.setAttribute('aria-label', 'Add to cart');
    }

    const tabIndex = visible ? '0' : '-1';
    if (clone.getAttribute('tabindex') !== tabIndex) {
      clone.setAttribute('tabindex', tabIndex);
    }

    if (clone.getAttribute('aria-hidden') !== String(!visible)) {
      clone.setAttribute('aria-hidden', String(!visible));
    }

    if (visible) {
      if (clone.style.getPropertyValue('display') !== 'flex') {
        clone.style.setProperty('display', 'flex', 'important');
      }
    } else if (clone.style.getPropertyValue('display') !== 'none') {
      clone.style.setProperty('display', 'none', 'important');
    }

    if (!clone.dataset.crcKeyboardReady) {
      clone.dataset.crcKeyboardReady = 'true';
      clone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          clone.click();
        }
      });
    }
  }

  function restoreNativeAddToCart(selection = getUploadSelection()) {
    let restored = false;
    const uploadRequired = requiresUpload(selection.selectedValue);
    const clones = Array.from(
      document.querySelectorAll(uploadlyCloneSelector)
    );

    clones.forEach((clone) => {
      setUploadlyCloneState(clone, uploadRequired);
    });

    if (uploadRequired && clones.length) {
      return false;
    }

    document.querySelectorAll(addToCartSelector).forEach((button) => {
      const form = button.form;
      const buyButtons = button.closest('.product-info__block--buy-buttons');

      if (buyButtons?.hidden || buyButtons?.getAttribute('aria-hidden') === 'true') {
        buyButtons.hidden = false;
        buyButtons.removeAttribute('aria-hidden');
        restored = true;
      }

      form?.querySelectorAll(
        'input[name="form_type"], input[name="utf8"], input[name="id"]'
      ).forEach((input) => {
        if (input.disabled) {
          input.disabled = false;
          restored = true;
        }
      });

      if (!uploadRequired && button.classList.contains(uploadlyButtonClass)) {
        button.classList.remove(uploadlyButtonClass);
        restored = true;
      }

      if (
        button.style.getPropertyValue('display') === 'none' ||
        button.style.getPropertyValue('opacity') !== '' ||
        button.style.getPropertyValue('visibility') !== ''
      ) {
        button.style.removeProperty('display');
        button.style.removeProperty('opacity');
        button.style.removeProperty('visibility');
        restored = true;
      }
    });

    return restored;
  }

  function getRequiredMessage() {
    const configuredField = Object.values(window.UPLOADCARE_DATA || {}).find(
      (field) => field?.field_name === 'Logo or photo upload'
    );

    return String(
      configuredField?.fileRequiredMessage ||
      'Please upload your logo or photo before adding this item to cart.'
    ).replace(/<[^>]*>/g, '');
  }

  function ensureUploadError(field) {
    let error = field.querySelector(uploadErrorSelector);

    if (!error) {
      error = document.createElement('p');
      error.id = `${field.id || 'crc-logo-photo-upload'}-error`;
      error.dataset.crcUploadError = 'true';
      error.hidden = true;
      error.setAttribute('role', 'alert');
      field.append(error);
    }

    error.textContent = getRequiredMessage();
    return error;
  }

  function uploadIsComplete(field) {
    const previews = field.querySelector('[id^="previewImages_"]');
    return Boolean(previews?.children.length);
  }

  function setUploadErrorState(field, hasError) {
    const error = ensureUploadError(field);
    const openButton = field.querySelector(
      '.uploadcare--widget__button_type_open'
    );

    error.hidden = !hasError;
    openButton?.setAttribute('aria-invalid', String(hasError));

    if (openButton) {
      const descriptions = new Set(
        String(openButton.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter(Boolean)
      );

      if (hasError) {
        descriptions.add(error.id);
      } else {
        descriptions.delete(error.id);
      }

      if (descriptions.size) {
        openButton.setAttribute(
          'aria-describedby',
          Array.from(descriptions).join(' ')
        );
      } else {
        openButton.removeAttribute('aria-describedby');
      }
    }
  }

  function syncUploaderVisibility(selection = getUploadSelection()) {
    if (typeof document.querySelector !== 'function') {
      return false;
    }

    const field = document.querySelector(uploaderSelector);

    if (!field) {
      return false;
    }

    const shouldShow = shouldShowUploader(
      selection.selectedValue,
      selection.groupHasUploads
    );

    field.hidden = !shouldShow;
    field.setAttribute('aria-hidden', String(!shouldShow));
    field.setAttribute('aria-required', String(shouldShow));

    if (shouldShow) {
      field.style.removeProperty('display');
      if (uploadIsComplete(field)) {
        setUploadErrorState(field, false);
      } else {
        ensureUploadError(field);
      }
    } else {
      field.style.setProperty('display', 'none', 'important');
      setUploadErrorState(field, false);
    }

    return true;
  }

  function syncProductFormState() {
    const selection = getUploadSelection();

    syncUploaderVisibility(selection);
    restoreNativeAddToCart(selection);
  }

  function validateRequiredUpload() {
    const selection = getUploadSelection();

    if (!requiresUpload(selection.selectedValue)) {
      return true;
    }

    const field = document.querySelector(uploaderSelector);

    if (field && uploadIsComplete(field)) {
      setUploadErrorState(field, false);
      return true;
    }

    if (field) {
      field.hidden = false;
      field.style.removeProperty('display');
      field.setAttribute('aria-hidden', 'false');
      setUploadErrorState(field, true);
      field.querySelector(
        '.uploadcare--widget__button_type_open'
      )?.focus();
    }

    return false;
  }

  function mutationNeedsSync(mutation) {
    if (mutation.type === 'attributes') {
      return (
        mutation.target.matches(addToCartSelector) ||
        mutation.target.matches(uploadlyCloneSelector) ||
        mutation.target.matches('.product-info__block--buy-buttons') ||
        mutation.target.matches('input[name="id"]')
      );
    }

    return Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return (
        node.matches(addToCartSelector) ||
        node.matches(uploadlyCloneSelector) ||
        node.matches(uploaderSelector) ||
        node.querySelector(addToCartSelector) ||
        node.querySelector(uploadlyCloneSelector) ||
        node.querySelector(uploaderSelector)
      );
    });
  }

  function initialize() {
    syncProductFormState();

    if (typeof MutationObserver === 'function' && document.body) {
      const observer = new MutationObserver((mutations) => {
        if (mutations.some(mutationNeedsSync)) {
          syncProductFormState();
        }
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled'],
        childList: true,
        subtree: true,
      });
    }
  }

  window.CRCUploadlyVariantSync = {
    requiresUpload,
    shouldShowUploader,
    getUploadSelection,
    restoreNativeAddToCart,
    syncUploaderVisibility,
    syncProductFormState,
    uploadIsComplete,
    validateRequiredUpload,
  };

  document.addEventListener('change', (event) => {
    const input = event.target;

    if (
      input instanceof HTMLInputElement &&
      input.type === 'radio'
    ) {
      syncProductFormState();
    }
  });

  document.addEventListener('on:variant:change', syncProductFormState);

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (
      !(form instanceof HTMLFormElement) ||
      !form.matches('form[action*="/cart/add"]') ||
      !form.querySelector(addToCartSelector) ||
      validateRequiredUpload()
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
